import { useCallback, useState } from "react";
import { Spoiler as MantineSpoiler } from "@mantine/core";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { componentWrapper } from "../../utilities";
import { Box } from "../box";
import { Icon } from "../icon";
import { Group } from "../group";
import type { CustomSpoilerFactory, IProps, ISpoilerChevronProps } from "./spoiler.types";
import type { SpoilerFactory } from "../../mantine";
import styles from "./spoiler.module.scss";
import { Stack } from "../stack";

/**
 * A React component that renders a spoiler.
 * @param children - The content of the spoiler.
 * @param expanded - Whether the spoiler is expanded.
 * @param onExpandedChange - The function to call when the spoiler is expanded.
 * @param showLabel - The label to show when the spoiler is collapsed.
 * @param hideLabel - The label to show when the spoiler is expanded.
 * @param props - The props to pass to the spoiler.
 * @returns A React component that renders a spoiler.
 */
const Spoiler = componentWrapper<CustomSpoilerFactory, IProps>(
    "Spoiler",
    ({ children, expanded, onExpandedChange, showLabel, hideLabel, ...props }, ref) => {
        const [isExpanded, setIsExpanded] = useState(expanded ?? false);

        const handleExpandedChange = useCallback(
            (value: boolean) => {
                setIsExpanded(value);
                onExpandedChange?.(value);
            },
            [onExpandedChange]
        );

        return (
            <MantineSpoiler
                classNames={{
                    root: styles["root"],
                    control: styles["control"],
                }}
                expanded={expanded ?? isExpanded}
                onExpandedChange={handleExpandedChange}
                showLabel={showLabel}
                hideLabel={hideLabel}
                {...props}
            >
                {children}
            </MantineSpoiler>
        );
    }
);

/**
 * A React component that renders a chevron for a spoiler.
 * @param children - The content of the spoiler.
 * @param expanded - Whether the spoiler is expanded.
 * @param onExpandedChange - The function to call when the spoiler is expanded.
 * @param trailingContent - The content to render after the spoiler. But can still be clicked to expand the spoiler.
 * @param props - The props to pass to the spoiler.
 * @returns A React component that renders a chevron for a spoiler.
 */
const SpoilerChevron = componentWrapper<SpoilerFactory, ISpoilerChevronProps>(
    "Spoiler.Chevron",
    ({ children, expanded, onExpandedChange, trailingContent, ...props }, ref) => {
        const [isExpanded, setIsExpanded] = useState(expanded ?? false);

        const handleExpandedChange = useCallback(
            (value: boolean) => {
                setIsExpanded(value);
                onExpandedChange?.(value);
            },
            [onExpandedChange]
        );

        const isOpen = expanded ?? isExpanded;

        return (
            <Group
                ref={ref}
                w="100%"
                gap="sm"
                className={styles["chevron-wrapper"]}
                onClick={() => handleExpandedChange(!isOpen)}
            >
                <Box component="span" flex="0 0 auto" pt="xxs">
                    <Icon icon={faChevronRight} size="xs" className={styles["chevron-icon"]} data-expanded={isOpen} />
                </Box>
                <Stack gap="sm">
                    <Spoiler
                        flex={1}
                        miw={0}
                        classNames={{
                            root: styles["chevron-root"],
                            control: styles["chevron-control"],
                        }}
                        expanded={isOpen}
                        onExpandedChange={handleExpandedChange}
                        {...props}
                        showLabel={<></>}
                        hideLabel={<></>}
                    >
                        {children}
                    </Spoiler>
                    {trailingContent}
                </Stack>
            </Group>
        );
    }
);

Spoiler.Chevron = SpoilerChevron;

export { Spoiler, SpoilerChevron };
